import React, { useState, useRef, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CompanyInfo, useCompanySettings } from '@/context/CompanySettingsContext';
import { uploadCompanyLogo } from '@/lib/fileHandler';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/hooks/use-toast";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from "@/components/ui/form";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Building2, 
  Facebook, 
  Globe, 
  Instagram, 
  Linkedin, 
  Mail, 
  Palette, 
  Phone, 
  User, 
  Youtube,
  Award,
  Upload,
  Link
} from "lucide-react";
import { Switch } from '@/components/ui/switch';

const companyInfoSchema = z.object({
  companyName: z.string().min(1, "Firmenname ist erforderlich"),
  contactPerson: z.string().min(1, "Kontaktperson ist erforderlich"),
  email: z.string().email("Ungültige E-Mail-Adresse"),
  phone: z.string().optional(),
  website: z.string().optional(),
  address: z.string().min(1, "Adresse ist erforderlich"),
  finmaNr: z.string().optional(),
  logoUrl: z.string().optional(),
  brandColors: z.object({
    primary: z.string(),
    secondary: z.string(),
    accent: z.string(),
  }),
  socialMedia: z.object({
    facebook: z.string().optional(),
    instagram: z.string().optional(),
    linkedin: z.string().optional(),
    youtube: z.string().optional(),
  }),
});

type CompanyInfoFormValues = z.infer<typeof companyInfoSchema>;

interface CompanySettingsModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const CompanySettingsModal: React.FC<CompanySettingsModalProps> = ({ open, onOpenChange }) => {
  const { companyInfo, updateCompanyInfo, isModalOpen: contextModalOpen, setIsModalOpen: setContextModalOpen, isLoading } = useCompanySettings();
  const [activeTab, setActiveTab] = useState("general");
  const [isUploading, setIsUploading] = useState(false);
  const [useDirectUrl, setUseDirectUrl] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const isModalOpen = open !== undefined ? open : contextModalOpen;
  const setIsModalOpen = onOpenChange || setContextModalOpen;

  useEffect(() => {
    if (companyInfo && !isLoading) {
      form.reset({
        companyName: companyInfo.companyName || "",
        contactPerson: companyInfo.contactPerson || "",
        email: companyInfo.email || "",
        phone: companyInfo.phone || "",
        website: companyInfo.website || "",
        address: companyInfo.address || "",
        finmaNr: companyInfo.finmaNr || "",
        logoUrl: companyInfo.logoUrl || "",
        brandColors: companyInfo.brandColors || {
          primary: "#18B08D",
          secondary: "#1A1F2C",
          accent: "#7E69AB",
        },
        socialMedia: companyInfo.socialMedia || {
          facebook: "",
          instagram: "",
          linkedin: "",
          youtube: "",
        },
      });
    }
  }, [companyInfo, isLoading]);

  const form = useForm<CompanyInfoFormValues>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      companyName: "",
      contactPerson: "",
      email: "",
      phone: "",
      website: "",
      address: "",
      finmaNr: "",
      logoUrl: "",
      brandColors: {
        primary: "#18B08D",
        secondary: "#1A1F2C",
        accent: "#7E69AB",
      },
      socialMedia: {
        facebook: "",
        instagram: "",
        linkedin: "",
        youtube: "",
      },
    },
  });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && user) {
      setIsUploading(true);
      try {
        const logoUrl = await uploadCompanyLogo(file, user.id);
        
        if (logoUrl) {
          form.setValue('logoUrl', logoUrl);
          toast({
            title: "Logo hochgeladen",
            description: "Das Logo wurde erfolgreich hochgeladen.",
          });
        } else {
          toast({
            title: "Fehler beim Hochladen",
            description: "Das Logo konnte nicht hochgeladen werden.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error uploading logo:', error);
        toast({
          title: "Fehler beim Hochladen",
          description: "Das Logo konnte nicht hochgeladen werden.",
          variant: "destructive"
        });
      } finally {
        setIsUploading(false);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const onSubmit = (data: CompanyInfoFormValues) => {
    console.log("Company info submitted:", data);
    updateCompanyInfo(data as CompanyInfo);
    toast({
      title: "Gespeichert",
      description: "CI einstellungen gespeichert.",
    });
    setIsModalOpen(false);
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className="bg-gradient-to-br from-black to-gray-900 text-white max-w-2xl max-h-[85vh] overflow-y-auto border border-gray-800">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white">CI Einstellungen</DialogTitle>
          <DialogDescription className="text-gray-400">
            Personalisieren Sie Ihre E-Mails mit Ihren Unternehmensinformationen.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jade"></div>
            <span className="ml-3 text-jade">Lade Einstellungen...</span>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-3 mb-6 bg-gray-800 border border-gray-700">
                  <TabsTrigger value="general" className="data-[state=active]:bg-jade text-white data-[state=active]:text-white">
                    Allgemein
                  </TabsTrigger>
                  <TabsTrigger value="colors" className="data-[state=active]:bg-jade text-white data-[state=active]:text-white">
                    Design
                  </TabsTrigger>
                  <TabsTrigger value="social" className="data-[state=active]:bg-jade text-white data-[state=active]:text-white">
                    Social Media
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-white">
                          <Building2 className="h-4 w-4 text-jade" />
                          Firmenname
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Ihre Firma GmbH"
                            {...field}
                            className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contactPerson"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-white">
                          <User className="h-4 w-4 text-jade" />
                          Kontaktperson
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Max Mustermann"
                            {...field}
                            className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-white">
                          <Mail className="h-4 w-4 text-jade" />
                          E-Mail
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="kontakt@ihrefirma.de"
                            {...field}
                            className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-white">
                          <Phone className="h-4 w-4 text-jade" />
                          Telefon
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="+49 123 4567890"
                            {...field}
                            className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-white">
                          <Globe className="h-4 w-4 text-jade" />
                          Website
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="www.ihrefirma.de"
                            {...field}
                            className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="finmaNr"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-white">
                          <Award className="h-4 w-4 text-jade" />
                          FINMA Nummer
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="FINMA-12345 (optional)"
                            {...field}
                            className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-white">
                          <Building2 className="h-4 w-4 text-jade" />
                          Adresse
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Musterstraße 1, 12345 Berlin"
                            {...field}
                            className="bg-gray-800 border-gray-700 text-white focus:border-jade min-h-[80px]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <FormLabel className="flex items-center gap-2 text-white">
                        <Upload className="h-4 w-4 text-jade" />
                        Firmenlogo
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-white text-sm">URL verwenden</FormLabel>
                        <Switch
                          checked={useDirectUrl}
                          onCheckedChange={setUseDirectUrl}
                        />
                      </div>
                    </div>

                    {useDirectUrl ? (
                      <FormField
                        control={form.control}
                        name="logoUrl"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <Link className="h-4 w-4 text-jade" />
                              <FormControl>
                                <Input
                                  placeholder="https://example.com/logo.png"
                                  {...field}
                                  className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                                />
                              </FormControl>
                            </div>
                            <FormDescription className="text-gray-400 text-xs">
                              Direkter Link zu Ihrem Logo (PNG oder SVG empfohlen).
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={triggerFileInput}
                          disabled={isUploading}
                          className="w-full border-gray-700 bg-gray-800 text-white hover:bg-gray-700"
                        >
                          {isUploading ? "Wird hochgeladen..." : form.watch('logoUrl') ? "Logo ersetzen" : "Logo hochladen"}
                        </Button>
                        {form.watch('logoUrl') && (
                          <div className="mt-2">
                            <FormDescription className="text-gray-400 text-xs mb-1">Aktuelles Logo:</FormDescription>
                            <div className="bg-gray-800 p-2 rounded border border-gray-700">
                              <img
                                src={form.watch('logoUrl')}
                                alt="Company Logo"
                                className="max-h-20 max-w-full object-contain"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="colors" className="space-y-4">
                  <div className="mb-2">
                    <FormLabel className="flex items-center gap-2 text-white mb-4">
                      <Palette className="h-4 w-4 text-jade" />
                      Markenfarben
                    </FormLabel>
                    <FormDescription className="text-gray-400 text-sm mb-4">
                      Wählen Sie die Farben, die Ihre Markenidentität repräsentieren. Diese Farben werden in E-Mails und Dokumenten verwendet.
                    </FormDescription>
                  </div>

                  <FormField
                    control={form.control}
                    name="brandColors.primary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Primärfarbe</FormLabel>
                        <div className="flex items-center gap-3">
                          <div 
                            className="h-8 w-8 rounded border border-gray-700"
                            style={{backgroundColor: field.value}}
                          />
                          <FormControl>
                            <Input
                              type="text"
                              {...field}
                              className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                            />
                          </FormControl>
                          <FormControl>
                            <Input
                              type="color"
                              {...field}
                              className="w-10 h-10 p-1 bg-transparent border-0"
                            />
                          </FormControl>
                        </div>
                        <FormDescription className="text-gray-400 text-xs">
                          Hauptfarbe für Überschriften und Schaltflächen.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="brandColors.secondary"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Sekundärfarbe</FormLabel>
                        <div className="flex items-center gap-3">
                          <div 
                            className="h-8 w-8 rounded border border-gray-700"
                            style={{backgroundColor: field.value}}
                          />
                          <FormControl>
                            <Input
                              type="text"
                              {...field}
                              className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                            />
                          </FormControl>
                          <FormControl>
                            <Input
                              type="color"
                              {...field}
                              className="w-10 h-10 p-1 bg-transparent border-0"
                            />
                          </FormControl>
                        </div>
                        <FormDescription className="text-gray-400 text-xs">
                          Unterstützende Farbe für Hintergründe.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="brandColors.accent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white">Akzentfarbe</FormLabel>
                        <div className="flex items-center gap-3">
                          <div 
                            className="h-8 w-8 rounded border border-gray-700"
                            style={{backgroundColor: field.value}}
                          />
                          <FormControl>
                            <Input
                              type="text"
                              {...field}
                              className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                            />
                          </FormControl>
                          <FormControl>
                            <Input
                              type="color"
                              {...field}
                              className="w-10 h-10 p-1 bg-transparent border-0"
                            />
                          </FormControl>
                        </div>
                        <FormDescription className="text-gray-400 text-xs">
                          Hervorhebungsfarbe für wichtige Elemente.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="social" className="space-y-4">
                  <div className="mb-2">
                    <FormLabel className="flex items-center gap-2 text-white mb-4">
                      <Globe className="h-4 w-4 text-jade" />
                      Social Media Links
                    </FormLabel>
                    <FormDescription className="text-gray-400 text-sm mb-4">
                      Fügen Sie Ihre Social-Media-Profile hinzu, damit Kunden Ihr Unternehmen besser kennenlernen können.
                    </FormDescription>
                  </div>

                  <FormField
                    control={form.control}
                    name="socialMedia.facebook"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <Facebook className="h-4 w-4 text-blue-500" />
                          <FormControl>
                            <Input
                              placeholder="https://facebook.com/ihrefirma"
                              {...field}
                              className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="socialMedia.instagram"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <Instagram className="h-4 w-4 text-pink-500" />
                          <FormControl>
                            <Input
                              placeholder="https://instagram.com/ihrefirma"
                              {...field}
                              className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="socialMedia.linkedin"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <Linkedin className="h-4 w-4 text-blue-600" />
                          <FormControl>
                            <Input
                              placeholder="https://linkedin.com/company/ihrefirma"
                              {...field}
                              className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="socialMedia.youtube"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <Youtube className="h-4 w-4 text-red-500" />
                          <FormControl>
                            <Input
                              placeholder="https://youtube.com/c/ihrefirma"
                              {...field}
                              className="bg-gray-800 border-gray-700 text-white focus:border-jade"
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>
              </Tabs>

              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  className="bg-jade hover:bg-jade-light text-white"
                >
                  Speichern
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
